import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Dynamic imports for syntax highlighter to avoid build issues
let SyntaxHighlighter: any = null;
let tomorrow: any = null;

try {
  const syntaxHighlighterModule = require('react-syntax-highlighter');
  const stylesModule = require('react-syntax-highlighter/dist/esm/styles/prism');
  SyntaxHighlighter = syntaxHighlighterModule.Prism;
  tomorrow = stylesModule.tomorrow;
} catch (error) {
  console.warn('Syntax highlighter not available, using fallback');
}

interface PerfectMarkdownRendererProps {
  content: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Perfect Markdown Renderer with advanced features
 * - Supports tables, task lists, strikethrough, autolinks
 * - Beautiful syntax highlighting for code blocks
 * - Enhanced image rendering with captions and error handling
 * - Professional typography and spacing
 * - Perfect for preview modes across all pages
 */
export default function PerfectMarkdownRenderer({
  content,
  className = '',
  style = {}
}: PerfectMarkdownRendererProps) {
  // Handle empty content gracefully
  if (!content || content.trim() === '') {
    return (
      <div className="text-gray-500 italic text-sm">
        [No content available for this section]
      </div>
    );
  }

  return (
    <div
      className={`prose prose-lg max-w-none ${className}`}
      style={{
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        lineHeight: '1.7',
        color: '#1f2937',
        ...style
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Enhanced headings with perfect spacing
          h1: ({ children }) => (
            <h1 className="text-4xl font-bold text-gray-900 mb-6 mt-8 first:mt-0 border-b-2 border-blue-200 pb-3">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-3xl font-semibold text-gray-800 mb-5 mt-8 border-l-4 border-blue-500 pl-4">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-6">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xl font-medium text-gray-800 mb-3 mt-5">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="text-lg font-medium text-gray-800 mb-3 mt-4">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="text-base font-medium text-gray-800 mb-2 mt-4">
              {children}
            </h6>
          ),

          // Perfect paragraph spacing
          p: ({ children }) => (
            <p className="mb-4 text-gray-700 leading-relaxed text-base">
              {children}
            </p>
          ),

          // Enhanced lists with proper spacing
          ul: ({ children }) => (
            <ul className="list-disc list-outside ml-6 mb-6 space-y-2">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside ml-6 mb-6 space-y-2">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-gray-700 leading-relaxed">
              {children}
            </li>
          ),

          // Beautiful blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-6 py-4 my-6 italic text-blue-900 rounded-r-lg shadow-sm">
              {children}
            </blockquote>
          ),

          // Enhanced code blocks with syntax highlighting
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            return !inline && match ? (
              <div className="my-6 rounded-lg overflow-hidden shadow-lg">
                <div className="bg-gray-800 px-4 py-2 text-sm text-gray-300 font-medium">
                  {language.toUpperCase()}
                </div>
                {SyntaxHighlighter && tomorrow ? (
                  <SyntaxHighlighter
                    style={tomorrow}
                    language={language}
                    PreTag="div"
                    className="!mt-0 !mb-0"
                    customStyle={{
                      margin: 0,
                      borderRadius: 0,
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <pre className="bg-gray-900 text-green-400 p-4 m-0 text-sm overflow-x-auto font-mono">
                    <code>{String(children).replace(/\n$/, '')}</code>
                  </pre>
                )}
              </div>
            ) : (
              <code 
                className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono" 
                {...props}
              >
                {children}
              </code>
            );
          },

          // Perfect table rendering
          table: ({ children }) => (
            <div className="my-6 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full border-collapse bg-white">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-gray-200">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50 transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b border-gray-100">
              {children}
            </td>
          ),

          // Enhanced image rendering with captions and error handling
          img: ({ src, alt, title, width, height, style, ...props }) => {
            const [loaded, setLoaded] = React.useState(false);
            const [error, setError] = React.useState(false);

            // Parse custom dimensions from title attribute (our special syntax)
            let customWidth = width || props.width;
            let customHeight = height || props.height;

            // Check if dimensions are encoded in title attribute
            if (title && title.includes('width=') || title && title.includes('height=')) {
              const widthMatch = title.match(/width=(\d+)/);
              const heightMatch = title.match(/height=(\d+)/);
              if (widthMatch) customWidth = parseInt(widthMatch[1]);
              if (heightMatch) customHeight = parseInt(heightMatch[1]);
            }

            const customStyle = style || props.style || {};

            // Create style object that preserves custom dimensions
            const imageStyle = {
              objectFit: 'contain' as const,
              ...customStyle,
            };

            // If custom dimensions are provided, use them; otherwise use responsive defaults
            if (customWidth || customHeight) {
              if (customWidth) imageStyle.width = typeof customWidth === 'number' ? `${customWidth}px` : customWidth;
              if (customHeight) imageStyle.height = typeof customHeight === 'number' ? `${customHeight}px` : customHeight;
              // Don't override custom dimensions with max constraints
            } else {
              // Only apply responsive constraints if no custom dimensions
              imageStyle.maxWidth = '100%';
              imageStyle.height = 'auto';
              imageStyle.maxHeight = '500px';
            }

            return (
              <div className="my-6 text-center">
                <div className="inline-block max-w-full">
                  {!error ? (
                    <img
                      src={src}
                      alt={alt || 'Image'}
                      title={title}
                      width={customWidth}
                      height={customHeight}
                      className={`rounded-lg shadow-lg border border-gray-200 transition-opacity duration-300 ${
                        loaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={() => setLoaded(true)}
                      onError={() => {
                        console.warn(`Failed to load image: ${src}`);
                        setError(true);
                      }}
                      style={imageStyle}
                      {...props}
                    />
                  ) : (
                    <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500">Image not available</p>
                      {alt && (
                        <p className="text-xs text-gray-400 mt-1">{alt}</p>
                      )}
                    </div>
                  )}
                  
                  {(alt || title) && !error && (
                    <figcaption className="text-sm text-gray-600 mt-2 italic">
                      {title || alt}
                    </figcaption>
                  )}
                </div>
              </div>
            );
          },

          // Enhanced links
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-blue-600 hover:text-blue-800 underline transition-colors"
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),

          // Horizontal rules
          hr: () => (
            <hr className="my-8 border-t-2 border-gray-200" />
          ),

          // Text formatting
          strong: ({ children }) => (
            <strong className="font-bold text-gray-900">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-800">{children}</em>
          ),
          del: ({ children }) => (
            <del className="line-through text-gray-500">{children}</del>
          ),

          // Task lists (GitHub Flavored Markdown)
          input: ({ checked, type, ...props }) => {
            if (type === 'checkbox') {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  className="mr-2 rounded"
                  {...props}
                />
              );
            }
            return <input type={type} {...props} />;
          },
        }}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}